import { useState } from 'react';
import SignInWithGoogleButton from '../components/SignInWithGoogleButton';
import SignInWithEmailButton from '../components/SignInWithEmailButton';
import ItemRow from '../components/ItemRow';
import ItemActionButton from '../components/ItemActionButton';
import Sorted from '../components/Sorted';
import Paginated from '../components/Paginated';
import useAuth from '../contexts/useAuth';
import usePlayer from '../contexts/usePlayer';
import useToast from '../contexts/useToast';
import { formatTime } from '../utils';
import type { ListData } from '../../../types';

type Panel = 'queue' | 'history';

const authButtonStyle: React.CSSProperties = {
  width: '100%',
  maxWidth: 280,
  height: 40,
  borderRadius: 20,
  border: 'none',
  background: 'var(--accent)',
  color: 'white',
  fontFamily: "'Roboto', arial, sans-serif",
  fontSize: 14,
  fontWeight: 500,
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const panelButton = (active: boolean): React.CSSProperties => ({
  flex: 1,
  borderRadius: 0,
  borderColor: active ? 'var(--gold)' : 'var(--border)',
  background: active ? 'var(--gold)' : 'var(--card-bg)',
  color: active ? 'var(--background)' : 'var(--primary)',
});

export default function Profile() {
  const { user, logout } = useAuth();
  const { nowPlayingId, play, isPlaying } = usePlayer();
  const { showToast } = useToast();
  const [panel, setPanel] = useState<Panel>('queue');
  const [queueVersion, setQueueVersion] = useState(0);
  const isAdmin = user?.isAdmin;

  return (
    <div className="page">
      <div style={{ padding: '16px 10px 10px' }}>
        {user?.email ? (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: 12,
              alignItems: 'center',
            }}
          >
            <span className="subtle-text" style={{ overflowWrap: 'anywhere' }}>
              {user.email}
            </span>
            <button onClick={logout} className="pill-button">
              Logout
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <SignInWithGoogleButton />
            <SignInWithEmailButton buttonStyle={authButtonStyle} />
          </div>
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 18,
            padding: '14px 0',
            borderTop: '1px solid var(--border)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <span>Total time listened</span>
          <strong>{formatTime(user?.seconds ?? 0)}</strong>
        </div>
      </div>

      <div style={{ display: 'flex', padding: '0 10px 12px' }}>
        <button
          className="pill-button"
          onClick={() => setPanel('queue')}
          style={panelButton(panel === 'queue')}
        >
          Queue
        </button>
        <button
          className="pill-button"
          onClick={() => setPanel('history')}
          style={panelButton(panel === 'history')}
        >
          History
        </button>
      </div>

      {panel === 'queue' ? (
        <Sorted<ListData>
          route="/api/queue"
          render={(item) => (
            <ItemRow key={item.id} data={item} noMargin isAdmin={isAdmin} />
          )}
          deps={[nowPlayingId, queueVersion]}
          emptyMessage="Queue is empty"
          onRemove={async (item) => {
            await fetch('/api/queue/remove', {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ contentId: item.id }),
            });
            showToast('Removed from queue', 'info', true, {
              label: 'Undo',
              onClick: async () => {
                await fetch('/api/queue/add', {
                  method: 'POST',
                  credentials: 'include',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ contentId: item.id }),
                });
                setQueueVersion((v) => v + 1);
              },
            });
          }}
          onReorder={async (reordered) => {
            await fetch('/api/queue/reorder', {
              method: 'POST',
              credentials: 'include',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                positions: reordered.map((queueItem, index) => ({
                  contentId: queueItem.id,
                  queuePosition: index,
                })),
              }),
            });
            const newTop = reordered[0];
            if (newTop && newTop.id !== nowPlayingId) {
              play(newTop, false, isPlaying);
            }
          }}
        />
      ) : (
        <Paginated<ListData>
          route="/api/history"
          render={(item) => (
            <div
              key={`history-${item.id}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                paddingRight: 10,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <ItemRow data={item} isAdmin={isAdmin} />
              </div>
              <ItemActionButton item={item} />
            </div>
          )}
          emptyMessage="No history yet"
        />
      )}
    </div>
  );
}
